import React from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
    TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, Switch
} from '@mui/material';
import IconIncome from '@mui/icons-material/NorthEastRounded';
import IconExpense from '@mui/icons-material/SouthWestRounded';
import IconRepeat from '@mui/icons-material/AutorenewRounded';
import styles from './TransactionForm.module.css';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        height: '40px',
        borderRadius: 'var(--border-radius-sm)'
    }
};

const selectSx = {
    height: '40px',
    borderRadius: 'var(--border-radius-sm)'
};

const TYPES = [
    { value: 'expense', label: 'Despesa', icon: <IconExpense fontSize="small" /> },
    { value: 'income', label: 'Receita', icon: <IconIncome fontSize="small" /> }
];

const datePickerProps = (extra = {}) => ({
    textField: { fullWidth: true, required: true, sx: inputSx, ...extra }
});

/** Formulário de nova transação / edição (mesmos campos e regras de antes). */
const TransactionForm = React.memo(({
    formData,
    handleFormChange,
    handleAmountBlur,
    amountError,
    categories,
    handleDateChange,
    handleRecurrenceStartDateChange,
    handleRecurrenceEndDateChange,
    handleHasEndDateChange
}) => {
    const changeType = (value) => handleFormChange({ target: { name: 'type', value } });
    const missingEndDate = formData.hasEndDate && !formData.recurrenceEndDate;

    return (
        <div className={styles.form}>
            {/* Tipo: par de botões no lugar do select, mesmo campo */}
            <div className={styles.typeToggle} role="radiogroup" aria-label="Tipo">
                {TYPES.map(option => {
                    const active = formData.type === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={`${styles.typeOption} ${active ? styles[`active_${option.value}`] : ''}`}
                            onClick={() => changeType(option.value)}
                        >
                            {option.icon}
                            {option.label}
                        </button>
                    );
                })}
            </div>

            <TextField
                fullWidth
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                required
                sx={inputSx}
            />

            <div className={styles.pair}>
                {/* Aceita "1.200,99", "1200,99" e "1200": o número só é lido ao salvar */}
                <TextField
                    fullWidth
                    label="Valor"
                    name="amount"
                    value={formData.amount}
                    onChange={handleFormChange}
                    onBlur={handleAmountBlur}
                    placeholder="0,00"
                    error={!!amountError}
                    helperText={amountError}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                        startAdornment: <span className={styles.prefix}>R$</span>
                    }}
                    required
                    sx={inputSx}
                />
                {/* Na recorrência vale só a "Data Início", logo abaixo */}
                {!formData.isRecurring && (
                    <DatePicker
                        label="Data"
                        value={dayjs(formData.transactionDate)}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        slotProps={datePickerProps()}
                    />
                )}
            </div>

            <FormControl fullWidth required>
                <InputLabel>Categoria</InputLabel>
                <Select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleFormChange}
                    label="Categoria"
                    required
                    sx={selectSx}
                >
                    <MenuItem value="">Selecione uma categoria</MenuItem>
                    {categories
                        .filter(cat => cat.type == formData.type)
                        .map(category => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))
                    }
                </Select>
            </FormControl>

            <div className={`${styles.recurrence} ${formData.isRecurring ? styles.recurrenceOn : ''}`}>
                <div className={styles.recurrenceHead}>
                    <span className={styles.recurrenceIcon}><IconRepeat fontSize="small" /></span>
                    <FormControlLabel
                        className={styles.recurrenceToggle}
                        control={
                            <Checkbox
                                name="isRecurring"
                                checked={formData.isRecurring}
                                onChange={handleFormChange}
                            />
                        }
                        label="Transação recorrente"
                    />
                </div>

                {formData.isRecurring && (
                    <div className={styles.recurrenceFields}>
                        <div className={styles.pair}>
                            <FormControl fullWidth>
                                <InputLabel>Frequência</InputLabel>
                                <Select
                                    name="recurrenceFrequency"
                                    value={formData.recurrenceFrequency}
                                    onChange={handleFormChange}
                                    label="Frequência"
                                    sx={selectSx}
                                >
                                    <MenuItem value="daily">Diária</MenuItem>
                                    <MenuItem value="weekly">Semanal</MenuItem>
                                    <MenuItem value="monthly">Mensal</MenuItem>
                                    <MenuItem value="yearly">Anual</MenuItem>
                                </Select>
                            </FormControl>
                            <DatePicker
                                label="Data Início"
                                value={dayjs(formData.recurrenceStartDate)}
                                onChange={handleRecurrenceStartDateChange}
                                format="DD/MM/YYYY"
                                slotProps={datePickerProps()}
                            />
                        </div>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!formData.hasEndDate}
                                    onChange={handleHasEndDateChange}
                                />
                            }
                            label="Definir data de término"
                        />

                        {formData.hasEndDate && (
                            <DatePicker
                                label="Data Fim"
                                value={formData.recurrenceEndDate ? dayjs(formData.recurrenceEndDate) : null}
                                onChange={handleRecurrenceEndDateChange}
                                format="DD/MM/YYYY"
                                minDate={dayjs(formData.recurrenceStartDate)}
                                slotProps={datePickerProps({
                                    error: missingEndDate,
                                    helperText: missingEndDate ? 'Data de término é obrigatória quando habilitada' : ''
                                })}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

TransactionForm.displayName = 'TransactionForm';

export default TransactionForm;
